import { useMemo, useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { Download } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { ventasPetshop } from '@/services/firebase/petshops';
import { usePetshopsMeta } from '@/hooks/usePetshopsMeta';
import { exportPetshopExcel } from '@/services/export';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs, formatDate } from '@/utils/format';
import type { PetshopId, PetshopVenta } from '@/types/domain';

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

interface Props {
  petshop: PetshopId;
}

type Preset = 'hoy' | 'ayer' | 'semana' | 'mes' | 'año' | 'todo' | 'custom';

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mes' },
  { id: 'año', label: 'Año' },
  { id: 'todo', label: 'Todo' },
];

function rangoDePreset(preset: Preset): { desde: Date | null; hasta: Date | null } {
  const now = new Date();
  const inicioHoy = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const finHoy = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  switch (preset) {
    case 'hoy':
      return { desde: inicioHoy, hasta: finHoy };
    case 'ayer': {
      const d = new Date(inicioHoy);
      d.setDate(d.getDate() - 1);
      const h = new Date(finHoy);
      h.setDate(h.getDate() - 1);
      return { desde: d, hasta: h };
    }
    case 'semana': {
      const d = new Date(inicioHoy);
      d.setDate(d.getDate() - 6); // últimos 7 días
      return { desde: d, hasta: finHoy };
    }
    case 'mes':
      return { desde: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0), hasta: finHoy };
    case 'año':
      return { desde: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0), hasta: finHoy };
    default:
      return { desde: null, hasta: null };
  }
}

export function ResumenTab({ petshop }: Props) {
  const { metaDe } = usePetshopsMeta();
  const ventasQ = useMemo(() => ventasPetshop(petshop), [petshop]);
  const { data: ventas, loading } = useFirestoreCollection(ventasQ);
  const [preset, setPreset] = useState<Preset>('mes');
  const [desdeStr, setDesdeStr] = useState('');
  const [hastaStr, setHastaStr] = useState('');

  const { desde, hasta } = useMemo(() => {
    if (preset === 'custom') {
      return {
        desde: desdeStr ? new Date(`${desdeStr}T00:00:00`) : null,
        hasta: hastaStr ? new Date(`${hastaStr}T23:59:59`) : null,
      };
    }
    return rangoDePreset(preset);
  }, [preset, desdeStr, hastaStr]);

  const ventasFiltradas = useMemo(() => {
    // Las ventas anuladas no cuentan en la evaluación.
    const base = ventas.filter((v) => !v.anulada);
    const arr = !desde && !hasta
      ? base
      : base.filter((v) => {
          const f = v.fecha?.toDate?.();
          if (!f) return true;
          if (desde && f < desde) return false;
          if (hasta && f > hasta) return false;
          return true;
        });
    return [...arr].sort((a, b) => (b.fecha?.toMillis?.() ?? 0) - (a.fecha?.toMillis?.() ?? 0));
  }, [ventas, desde, hasta]);

  const total = ventasFiltradas.reduce((s, v) => s + (v.monto ?? 0), 0);

  const periodoTxt =
    preset === 'todo' || (!desde && !hasta)
      ? 'Todas las ventas'
      : `${desde ? formatDate(desde) : '—'} → ${hasta ? formatDate(hasta) : 'hoy'}`;

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      {/* Controles: Desde/Hasta a la izquierda · botones de período (chicos) a la derecha */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          padding: 14,
        }}
      >
        {Platform.OS === 'web' ? (
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <FechaInput
              label="Desde"
              value={desdeStr}
              onChange={(v) => {
                setDesdeStr(v);
                setPreset('custom');
              }}
            />
            <FechaInput
              label="Hasta"
              value={hastaStr}
              onChange={(v) => {
                setHastaStr(v);
                setPreset('custom');
              }}
            />
          </View>
        ) : (
          <View />
        )}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
          {PRESETS.map((p) => (
            <ChipBtn key={p.id} label={p.label} active={preset === p.id} onPress={() => setPreset(p.id)} />
          ))}
        </View>
      </View>

      {/* Total: una fila arriba de todas las ventas */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          paddingHorizontal: 16,
          paddingVertical: 12,
          backgroundColor: colors.crema[50],
          borderTopWidth: 1,
          borderBottomWidth: 1,
          borderColor: colors.ink[100],
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: colors.success[600] }}>
            {formatBs(total)}
          </Text>
          <Text style={{ fontSize: 12, color: colors.ink[500] }}>
            {ventasFiltradas.length} {ventasFiltradas.length === 1 ? 'venta' : 'ventas'} · {periodoTxt}
          </Text>
        </View>
        {ventasFiltradas.length > 0 ? (
          <Button
            label="Excel"
            variant="success"
            size="sm"
            iconLeft={<Download size={14} color={colors.white} />}
            onPress={async () => {
              try {
                await exportPetshopExcel({ petshop, petshopNombre: metaDe(petshop).nombre, ventas: ventasFiltradas, rendiciones: [] });
              } catch (e: any) {
                alert(e?.message ?? 'No se pudo exportar.');
              }
            }}
          />
        ) : null}
      </View>

      {/* Lista de ventas del período */}
      {loading ? (
        <View style={{ padding: 16 }}>
          <SkeletonRows rows={4} />
        </View>
      ) : ventasFiltradas.length === 0 ? (
        <View style={{ padding: 12 }}>
          <EmptyState illustration="note" title="Sin ventas en este período" message="Probá con otro período o cargá ventas." />
        </View>
      ) : (
        ventasFiltradas.map((v, idx) => (
          <View
            key={v.id}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              paddingHorizontal: 16,
              paddingVertical: 11,
              backgroundColor: idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
              borderBottomWidth: idx === ventasFiltradas.length - 1 ? 0 : 1,
              borderBottomColor: colors.ink[100],
            }}
          >
            <View style={{ width: 86 }}>
              <Text style={{ fontSize: 12, color: colors.ink[700] }}>{formatDate(v.fecha.toDate())}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13.5, color: colors.ink[900], fontWeight: typography.weights.semibold }} numberOfLines={1}>
                {v.descripcion}
              </Text>
              <Text style={{ fontSize: 11, color: colors.ink[500], marginTop: 1 }}>
                {METODO_LABEL[v.metodoPago] ?? v.metodoPago}
              </Text>
            </View>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 15, fontWeight: typography.weights.bold, color: colors.success[600] }}>
              {formatBs(v.monto)}
            </Text>
          </View>
        ))
      )}
    </Card>
  );
}

function ChipBtn({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        paddingHorizontal: 11,
        height: 28,
        borderRadius: 999,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: active ? colors.magenta[700] : hovered ? colors.crema[100] : colors.crema[50],
        borderWidth: 1,
        borderColor: active ? colors.magenta[700] : colors.ink[200],
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <Text style={{ color: active ? colors.white : colors.ink[600], fontWeight: typography.weights.bold, fontSize: 11.5 }}>
        {label}
      </Text>
    </Pressable>
  );
}

function FechaInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <View style={{ gap: 4 }}>
      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </Text>
      {/* @ts-ignore web-only native input */}
      <input
        type="date"
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
        style={{
          height: 40,
          padding: '0 12px',
          borderRadius: 10,
          border: `1.5px solid ${colors.ink[200]}`,
          background: colors.white,
          color: colors.ink[900],
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
          width: 145,
          boxSizing: 'border-box',
        }}
      />
    </View>
  );
}
